import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../ThemeContext';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── FileThumb — Thumbnail/player per un file dell'archivio ── */
function FileThumb({ file, fileUrl, isDark, accent, textSub, compact, onLightbox }) {
  const url = fileUrl(file.id);
  const ct = file.content_type || '';
  const isImg = ct.startsWith('image');
  const isVideo = ct.startsWith('video');
  const isAudio = ct.startsWith('audio');
  const name = file.original_filename || 'file';

  const openLightbox = () => {
    if (isImg) onLightbox({ type: 'image', url, name });
    else if (isVideo) onLightbox({ type: 'video', url, name });
    else if (isAudio) onLightbox({ type: 'audio', url, name });
  };

  const base = `rounded-lg overflow-hidden border cursor-pointer transition-all hover:scale-[1.03] ${isDark ? 'border-[#222] bg-[#0d0d0d]' : 'border-[#E2DED6] bg-white'}`;

  if (isImg) {
    return (
      <div className={`${base} ${compact ? 'aspect-square' : 'w-20 h-20'}`} onClick={openLightbox} data-testid="file-thumb-image">
        <img src={url} alt={name} className="w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className={`${base} ${compact ? 'aspect-square' : 'w-20 h-20'} relative`} onClick={openLightbox} data-testid="file-thumb-video">
        <video src={url} className="w-full h-full object-cover" muted preload="metadata" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
        </div>
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className={`${base} p-2 flex flex-col items-center justify-center ${compact ? 'aspect-square' : 'w-20 h-20'}`} onClick={openLightbox} data-testid="file-thumb-audio">
        <svg className="w-5 h-5 mb-1" style={{ color: accent }} fill="currentColor" viewBox="0 0 24 24"><path d="M12 3v10.55A4 4 0 1014 17V7h4V3h-6z"/></svg>
        <p className={`${textSub} text-[8px] truncate w-full text-center`}>{name}</p>
      </div>
    );
  }

  // PDF, text, etc — download link
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`${base} p-2 flex flex-col items-center justify-center no-underline ${compact ? 'aspect-square' : 'w-20 h-20'}`} data-testid="file-thumb-doc">
      <svg className="w-5 h-5 mb-1" style={{ color: accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
      <p className={`${textSub} text-[8px] truncate w-full text-center`}>{name}</p>
    </a>
  );
}

const CATEGORIES = [
  { id: 'ricetta', label: 'Ricetta', pts: 15 },
  { id: 'arte', label: 'Arte Antica', pts: 20 },
  { id: 'intervista', label: 'Intervista', pts: 25 },
  { id: 'sapienza', label: 'Sapienza', pts: 15 },
  { id: 'proverbio', label: 'Proverbio', pts: 10 },
  { id: 'video', label: 'Video', pts: 30 },
  { id: 'storia', label: 'Storia/Aneddoto', pts: 20 },
];

const MENTORS_INFO = {
  title: 'I Mentori',
  desc: 'Boomer, Silent Generation e Greatest Generation sono i MENTORI. Non competono: insegnano, raccontano storie, tramandano sapienza. Ogni racconto archiviato arricchisce il sapere collettivo.',
};

export default function ArchivioDigitalePage() {
  const { isInstitutional } = useTheme();
  const isDark = !isInstitutional;
  const green = '#00FF41';

  const [teams, setTeams] = useState([]);
  const [entries, setEntries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [tab, setTab] = useState('strategia');
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ team_id: '', title: '', category: 'ricetta', description: '', mentor_name: '', link: '' });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [teamFiles, setTeamFiles] = useState({});
  const [galleryTeam, setGalleryTeam] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [openSfida, setOpenSfida] = useState(null);
  const [sfidaForm, setSfidaForm] = useState({ description: '', link: '', photo: null, file: null });

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, eRes, lRes] = await Promise.all([
        fetch(`${API}/api/archive/teams`),
        fetch(`${API}/api/archive/entries`),
        fetch(`${API}/api/archive/leaderboard`),
      ]);
      setTeams(await tRes.json());
      setEntries(await eRes.json());
      const lb = await lRes.json();
      setLeaderboard(lb.leaderboard || []);
      setPrizes(lb.prizes || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch files per team
  useEffect(() => {
    if (teams.length === 0) return;
    const fetchFiles = async () => {
      const filesMap = {};
      await Promise.all(teams.map(async (t) => {
        try {
          const r = await fetch(`${API}/api/archive/files-list/${t.id}`);
          filesMap[t.id] = await r.json();
        } catch { filesMap[t.id] = []; }
      }));
      setTeamFiles(filesMap);
    };
    fetchFiles();
  }, [teams]);

  const createEntry = async () => {
    if (!newEntry.team_id || !newEntry.title.trim() || !newEntry.description.trim()) return;
    setUploading(true);
    try {
      // 1. Create the entry first
      setUploadProgress('Salvataggio contributo...');
      const entryRes = await fetch(`${API}/api/archive/entries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEntry),
      });
      const entry = await entryRes.json();

      // 2. Upload attached files
      if (uploadFiles.length > 0) {
        for (let i = 0; i < uploadFiles.length; i++) {
          setUploadProgress(`Upload file ${i + 1}/${uploadFiles.length}: ${uploadFiles[i].name}...`);
          const form = new FormData();
          form.append('file', uploadFiles[i]);
          form.append('team_id', newEntry.team_id);
          form.append('entry_id', entry.id || '');
          await fetch(`${API}/api/archive/upload`, { method: 'POST', body: form });
        }
      }

      setNewEntry({ team_id: '', title: '', category: 'ricetta', description: '', mentor_name: '', link: '' });
      setUploadFiles([]);
      setShowNewEntry(false);
      fetchAll();
    } catch (e) { console.error(e); }
    setUploading(false);
    setUploadProgress('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) setUploadFiles(prev => [...prev, ...files]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) setUploadFiles(prev => [...prev, ...files]);
  };

  const removeFile = (idx) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const submitSfida = async (sfida) => {
    if (!sfidaForm.description.trim()) return;
    setUploading(true);
    try {
      // Pick first team or default
      const teamId = teams.length > 0 ? teams[0].id : '';
      setUploadProgress('Salvataggio contributo...');
      const entryRes = await fetch(`${API}/api/archive/entries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team_id: teamId,
          title: sfida.title + ' — ' + sfida.by,
          category: sfida.cat === 'Ricetta' ? 'ricetta' : sfida.cat === 'Sapienza' ? 'sapienza' : 'arte',
          description: sfidaForm.description + (sfidaForm.link ? '\n\nLink: ' + sfidaForm.link : ''),
          mentor_name: sfida.by,
          link: sfidaForm.link,
        }),
      });
      const entry = await entryRes.json();

      const filesToUpload = [sfidaForm.photo, sfidaForm.file].filter(Boolean);
      for (let i = 0; i < filesToUpload.length; i++) {
        setUploadProgress(`Upload file ${i + 1}/${filesToUpload.length}...`);
        const form = new FormData();
        form.append('file', filesToUpload[i]);
        form.append('team_id', teamId);
        form.append('entry_id', entry.id || '');
        await fetch(`${API}/api/archive/upload`, { method: 'POST', body: form });
      }

      setSfidaForm({ description: '', link: '', photo: null, file: null });
      setOpenSfida(null);
      fetchAll();
    } catch (e) { console.error(e); }
    setUploading(false);
    setUploadProgress('');
  };

  // File URL helper
  const fileUrl = (fileId) => `${API}/api/archive/files/${fileId}`;

  // Get files for a specific entry
  const getEntryFiles = (entryId, teamId) => {
    const tFiles = teamFiles[teamId] || [];
    return entryId ? tFiles.filter(f => f.entry_id === entryId) : [];
  };

  // Get all files for a team
  const getTeamFiles = (teamId) => teamFiles[teamId] || [];

  const bg = isDark ? 'bg-[#050505]' : 'bg-[#FAF9F6]';
  const cardBg = isDark ? 'bg-[#0a0a0a] border-[#1a1a1a]' : 'bg-white border-[#E2DED6]';
  const text = isDark ? 'text-white' : 'text-[#1B2A4A]';
  const textSub = isDark ? 'text-zinc-400' : 'text-[#5A6B80]';
  const accent = isDark ? green : '#C4532D';
  const inputCls = isDark
    ? 'bg-[#111] border border-[#222] text-white placeholder-zinc-600 focus:border-[#00FF41] rounded-lg px-3 py-2 text-sm outline-none'
    : 'bg-[#F5F3EE] border border-[#E2DED6] text-[#1B2A4A] placeholder-[#8A7A62] focus:border-[#C4532D] rounded-lg px-3 py-2 text-sm outline-none';

  const TABS = [
    { id: 'strategia', label: 'Strategia' },
    { id: 'archivio', label: 'Archivio' },
    { id: 'squadre', label: 'Squadre' },
    { id: 'classifica', label: 'Classifica' },
    { id: 'premi', label: 'Premi' },
  ];

  return (
    <main data-testid="archivio-page" className={`min-h-screen pt-20 pb-16 ${bg} ${text}`}>
      {/* Hero */}
      <div className="relative h-[280px] md:h-[360px] overflow-hidden">
        <img src="/archivio_hero.png" alt="Archivio Digitale" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="absolute bottom-6 left-0 right-0 text-center px-6">
          <p className="font-mono font-black text-2xl md:text-4xl mb-2" style={{ color: green, textShadow: '0 0 20px rgba(0,255,65,0.3)' }} data-testid="archivio-title">&lt;/archivio_digitale&gt;</p>
          <p className="wave-text wave-text-slow" style={{ fontFamily: "'Georgia', serif", fontWeight: 700, fontStyle: 'italic', fontSize: '14px' }}>
            {'la sapienza di ieri, il cloud di domani'.split('').map((c, i) => (
              <span key={i} style={{ color: '#fff' }}>{c === ' ' ? '\u00A0' : c}</span>
            ))}
          </p>
        </div>
      </div>

      {/* Logo download */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-6 mb-2 flex justify-end">
        <a href="/logo_aicovo_transparent.png" download="AI_covo_tech_logo.png" className="font-mono text-[10px] px-3 py-1 rounded border transition-colors" style={{ borderColor: accent + '44', color: accent }} data-testid="download-logo-link">
          Scarica Logo PNG
        </a>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6">

        {/* ═══════ CTA ANCORATA — 100 PUNTI ═══════ */}
        <div data-testid="bounty-100" className="mb-6 border-2 rounded-xl p-4 relative overflow-hidden" 
          style={{ borderColor: '#FF2D2D', background: isDark ? '#1a0505' : '#FFF5F5', boxShadow: '0 0 20px rgba(255,45,45,0.15)' }}>
          <div className="absolute top-0 right-0 px-3 py-1 font-mono font-black text-xs text-white" style={{ background: '#FF2D2D' }}>
            BOUNTY
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center font-mono font-black text-2xl" 
              style={{ background: '#FF2D2D20', border: '3px solid #FF2D2D', color: '#FF2D2D' }}>
              100
            </div>
            <div className="flex-1">
              <p className="font-mono font-black text-sm" style={{ color: '#FF2D2D' }}>MISSIONE SPECIALE — 100 PUNTI</p>
              <p className="text-white font-bold text-xs mt-1">
                Informazioni sensibili e particolarmente importanti su Colle Val d'Elsa e il tessuto sociale toscano.
                Storie non raccontate, documenti rari, testimonianze esclusive.
              </p>
              <p className={`${textSub} text-[10px] font-mono mt-1`}>Valutazione: il team assegna da 50 a 100 punti in base alla rilevanza</p>
            </div>
          </div>
        </div>

        {/* ═══════ 6 SFIDE ATTIVE ═══════ */}
        <div className="mb-8">
          <p className="font-mono font-bold text-sm mb-4 flex items-center gap-2" style={{ color: accent }}>
            <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
            SFIDE ATTIVE — CHI LE COMPLETA GUADAGNA PUNTI
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { num: 1, title: 'Lavorazione del Cristallo', by: 'Loreno Grassini', pts: 30, cat: 'Arte Antica', desc: 'Documenta la lavorazione artigianale del cristallo colligiano con il Maestro Loreno Grassini. Video, foto, intervista.', color: '#00BFFF' },
              { num: 2, title: 'Cristallo Colligiano', by: 'Mario Belli', pts: 30, cat: 'Arte Antica', desc: 'L\'arte del cristallo di Colle Val d\'Elsa raccontata da Mario Belli. Tecniche, storia, passione.', color: '#A855F7' },
              { num: 3, title: 'Ricetta della Gota Cotta', by: 'Macelleria Gori', pts: 20, cat: 'Ricetta', desc: 'La ricetta colligiana della Gota Cotta come la prepara la storica Macelleria Gori. Ingredienti, procedimento, segreti.', color: '#FF6B6B' },
              { num: 4, title: 'Il Gelato di Una Volta', by: 'Gelateria Buekke', pts: 20, cat: 'Ricetta', desc: 'Come si faceva il gelato artigianale una volta? Lo racconta la Gelateria Buekke. Ricetta, storia, tradizione.', color: '#FFD700' },
              { num: 5, title: 'Cioccolato a Regola d\'Arte', by: 'Le Follie di Arnolfo', pts: 20, cat: 'Arte Antica', desc: 'L\'arte cioccolatiera di Le Follie di Arnolfo. Dalla fava di cacao alla praline: documenta il processo.', color: '#10B981' },
              { num: 6, title: 'Cappello in Paglia Toscano', by: 'Tradizione popolare', pts: 25, cat: 'Sapienza', desc: 'Come si realizza il tipico cappello in paglia toscano? Intervista chi ancora lo sa fare. Tecnica, materiali, storia.', color: '#FF8C00' },
            ].map((s) => (
              <div key={s.num} data-testid={`sfida-${s.num}`}
                className="border rounded-xl p-4 transition-all relative overflow-hidden group"
                style={{ borderColor: openSfida === s.num ? s.color : s.color + '44', background: isDark ? '#0a0a0a' : '#fff' }}
              >
                {/* Barra punti */}
                <div className="absolute top-0 right-0 px-2 py-0.5 font-mono font-black text-[10px] rounded-bl" style={{ background: s.color, color: '#000' }}>
                  {s.pts} PTS
                </div>
                <div className="flex items-start gap-3 cursor-pointer" onClick={() => { setOpenSfida(openSfida === s.num ? null : s.num); setSfidaForm({ description: '', link: '', photo: null, file: null }); }}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-mono font-black text-lg"
                    style={{ background: s.color + '15', border: `2px solid ${s.color}44`, color: s.color }}>
                    {s.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-white group-hover:underline" style={{ textDecorationColor: s.color }}>{s.title}</p>
                    <p className="font-mono text-[10px] font-bold" style={{ color: s.color }}>{s.by}</p>
                    <p className={`${textSub} text-[10px] mt-1 leading-relaxed`}>{s.desc}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="font-mono text-[9px] px-2 py-0.5 rounded-full border" style={{ borderColor: s.color + '44', color: s.color }}>{s.cat}</span>
                      <span className="font-mono text-[9px] font-bold" style={{ color: s.color }}>{openSfida === s.num ? 'CHIUDI' : 'PARTECIPA'}</span>
                    </div>
                  </div>
                </div>

                {/* ═══ FORM INLINE ═══ */}
                {openSfida === s.num && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: s.color + '33' }}>
                    <p className="font-mono font-bold text-xs mb-3" style={{ color: s.color }}>Inserisci il tuo contributo</p>
                    
                    <textarea
                      data-testid={`sfida-desc-${s.num}`}
                      value={sfidaForm.description}
                      onChange={e => setSfidaForm({ ...sfidaForm, description: e.target.value })}
                      placeholder="Descrivi il contributo: la storia, la ricetta, la tecnica..."
                      className={`${inputCls} w-full mb-3 h-20 resize-none`}
                    />
                    
                    <input
                      data-testid={`sfida-link-${s.num}`}
                      value={sfidaForm.link}
                      onChange={e => setSfidaForm({ ...sfidaForm, link: e.target.value })}
                      placeholder="Link (opzionale — YouTube, sito, social)"
                      className={`${inputCls} w-full mb-3`}
                    />

                    <div className="flex gap-2 mb-3">
                      <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs ${isDark ? 'border-[#222] bg-[#111]' : 'border-[#E2DED6] bg-white'}`}>
                        <svg className="w-4 h-4" style={{ color: s.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        <span style={{ color: s.color }} className="font-mono">{sfidaForm.photo ? sfidaForm.photo.name.slice(0, 20) : 'Foto'}</span>
                        <input type="file" accept="image/*" className="hidden" data-testid={`sfida-photo-${s.num}`}
                          onChange={e => setSfidaForm({ ...sfidaForm, photo: e.target.files[0] || null })} />
                      </label>
                      <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs ${isDark ? 'border-[#222] bg-[#111]' : 'border-[#E2DED6] bg-white'}`}>
                        <svg className="w-4 h-4" style={{ color: s.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                        <span style={{ color: s.color }} className="font-mono">{sfidaForm.file ? sfidaForm.file.name.slice(0, 20) : 'File'}</span>
                        <input type="file" accept="video/*,audio/*,.pdf,.txt,.doc,.docx" className="hidden" data-testid={`sfida-file-${s.num}`}
                          onChange={e => setSfidaForm({ ...sfidaForm, file: e.target.files[0] || null })} />
                      </label>
                    </div>

                    {uploading && (
                      <div className="mb-3 flex items-center gap-2">
                        <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: s.color, borderTopColor: 'transparent' }} />
                        <span className="font-mono text-[10px]" style={{ color: s.color }}>{uploadProgress}</span>
                      </div>
                    )}

                    <button
                      data-testid={`sfida-submit-${s.num}`}
                      onClick={() => submitSfida(s)}
                      disabled={uploading || !sfidaForm.description.trim()}
                      className="w-full font-mono text-xs font-bold py-2 rounded-lg transition-all disabled:opacity-30"
                      style={{ background: s.color, color: '#000' }}
                    >
                      {uploading ? 'INVIO IN CORSO...' : 'INVIA CONTRIBUTO'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="archivio-tabs">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`font-mono text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg border transition-all ${tab === t.id ? '' : 'opacity-60 hover:opacity-80'}`}
              style={tab === t.id ? { borderColor: accent, color: isDark ? '#000' : '#fff', background: accent } : { borderColor: accent + '44', color: accent, background: 'transparent' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* TAB: Strategia */}
        {tab === 'strategia' && (
          <div className="space-y-6">
            {/* La Missione */}
            <div className={`${cardBg} border rounded-xl p-6`} data-testid="archivio-mission">
              <p className="font-mono font-bold text-lg mb-3" style={{ color: accent }}>&lt;/missione&gt;</p>
              <p className={`${textSub} text-sm leading-relaxed mb-4`}>
                <strong className={text}>Vince il sapere. Vince la sapienza.</strong> I team competono per raccogliere e archiviare il patrimonio immateriale: 
                <strong style={{ color: accent }}> ricette, video, arti tramandate, proverbi, storie e aneddoti</strong> dei nostri anziani.
                Ogni contributo fa guadagnare punti. Ogni ricerca su <Link to="/mentor" className="font-mono font-bold underline" style={{ color: accent }}>&lt;/mentore&gt;</Link> diventa 
                più ricca grazie alla competizione.
              </p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => (
                  <span key={c.id} className="font-mono text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: accent + '44', color: accent, background: accent + '10' }}>
                    {c.label} — {c.pts} pts
                  </span>
                ))}
              </div>
            </div>

            {/* Come funziona */}
            <div className={`${cardBg} border rounded-xl p-6`}>
              <p className="font-mono font-bold text-lg mb-4" style={{ color: accent }}>&lt;/come_funziona&gt;</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4">
                  <p className="font-mono font-black text-3xl mb-2" style={{ color: accent }}>1</p>
                  <p className="font-bold text-sm mb-1">Scegli il tuo Team</p>
                  <p className={`${textSub} text-xs`}>Alpha, Beta, GenZ, Millennials o GenX</p>
                </div>
                <div className="text-center p-4">
                  <p className="font-mono font-black text-3xl mb-2" style={{ color: accent }}>2</p>
                  <p className="font-bold text-sm mb-1">Intervista i Mentori</p>
                  <p className={`${textSub} text-xs`}>Boomer, Silent e Greatest Generation raccontano la loro sapienza</p>
                </div>
                <div className="text-center p-4">
                  <p className="font-mono font-black text-3xl mb-2" style={{ color: accent }}>3</p>
                  <p className="font-bold text-sm mb-1">Archivia e Guadagna Punti</p>
                  <p className={`${textSub} text-xs`}>Ogni contributo = punti per il tuo team. I migliori vincono premi reali!</p>
                </div>
              </div>
            </div>

            {/* Mentori */}
            <div className={`${cardBg} border rounded-xl p-6`} data-testid="mentors-section">
              <p className="font-mono font-bold text-lg mb-3" style={{ color: accent }}>&lt;/i_mentori&gt;</p>
              <p className={`${textSub} text-sm leading-relaxed mb-4`}>{MENTORS_INFO.desc}</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                {['Boomer', 'Silent Generation', 'Greatest Generation'].map(gen => (
                  <div key={gen} className="p-3 rounded-lg border" style={{ borderColor: accent + '22', background: accent + '08' }}>
                    <p className="font-mono font-bold text-sm" style={{ color: accent }}>{gen}</p>
                    <p className={`${textSub} text-[10px] mt-1`}>MENTORE</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Link to="/mentor" className="zanfa-cta inline-block" data-testid="go-to-mentor">&lt;/mentore&gt;</Link>
                <p className={`${textSub} text-xs mt-2`}>Tutta la sapienza raccolta si trova qui</p>
              </div>
            </div>
          </div>
        )}

        {/* TAB: Archivio */}
        {tab === 'archivio' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="font-mono font-bold" style={{ color: accent }}>Contributi recenti</p>
              <button onClick={() => setShowNewEntry(!showNewEntry)} data-testid="btn-new-entry"
                className="font-mono text-xs font-bold px-4 py-2 rounded-lg transition-all"
                style={{ background: accent, color: isDark ? '#000' : '#fff' }}>
                + Nuovo Contributo
              </button>
            </div>
            {showNewEntry && (
              <div className={`${cardBg} border rounded-xl p-5 mb-6`} data-testid="new-entry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <select value={newEntry.team_id} onChange={e => setNewEntry({ ...newEntry, team_id: e.target.value })} className={inputCls} data-testid="entry-team-select">
                    <option value="">Scegli squadra...</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <select value={newEntry.category} onChange={e => setNewEntry({ ...newEntry, category: e.target.value })} className={inputCls} data-testid="entry-category-select">
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label} ({c.pts} pts)</option>)}
                  </select>
                </div>
                <input value={newEntry.title} onChange={e => setNewEntry({ ...newEntry, title: e.target.value })} placeholder="Titolo (es. Pappardelle al cinghiale della Nonna Rosa)" className={`${inputCls} w-full mb-3`} data-testid="entry-title" />
                <textarea value={newEntry.description} onChange={e => setNewEntry({ ...newEntry, description: e.target.value })} placeholder="Descrizione, ingredienti, storia, proverbio..." className={`${inputCls} w-full mb-3 h-24 resize-none`} data-testid="entry-description" />
                <input value={newEntry.mentor_name} onChange={e => setNewEntry({ ...newEntry, mentor_name: e.target.value })} placeholder="Nome del mentore intervistato (Boomer/Silent/Greatest)" className={`${inputCls} w-full mb-3`} data-testid="entry-mentor" />
                <input value={newEntry.link || ''} onChange={e => setNewEntry({ ...newEntry, link: e.target.value })} placeholder="Link correlato (opzionale — es. sito web, video YouTube)" className={`${inputCls} w-full mb-3`} data-testid="entry-link" />

                {/* ═══ DRAG & DROP ZONE ═══ */}
                <div
                  data-testid="drop-zone"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className={`w-full mb-3 border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                    dragOver
                      ? `border-[${accent}] bg-[${accent}11]`
                      : isDark ? 'border-[#333] bg-[#0a0a0a] hover:border-[#444]' : 'border-[#D0C8B8] bg-[#F5F3EE] hover:border-[#C4532D]'
                  }`}
                  style={dragOver ? { borderColor: accent, background: accent + '11' } : {}}
                  onClick={() => document.getElementById('file-input-archive').click()}
                >
                  <input
                    id="file-input-archive"
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*,.pdf,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-10 h-10" style={{ color: accent + '66' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="font-mono text-sm font-bold" style={{ color: accent }}>
                      {dragOver ? 'Rilascia qui!' : 'Trascina file qui'}
                    </p>
                    <p className={`${textSub} text-xs`}>oppure clicca per selezionare — foto, video, audio, PDF, testo (max 20MB)</p>
                  </div>
                </div>

                {/* File preview */}
                {uploadFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="file-previews">
                    {uploadFiles.map((f, i) => (
                      <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${isDark ? 'border-[#222] bg-[#111]' : 'border-[#E2DED6] bg-white'}`}>
                        <span className="font-mono" style={{ color: accent }}>
                          {f.type?.startsWith('image') ? '🖼' : f.type?.startsWith('video') ? '🎬' : f.type?.startsWith('audio') ? '🎙' : '📄'}
                        </span>
                        <span className={textSub + ' truncate max-w-[140px]'}>{f.name}</span>
                        <span className={textSub}>({(f.size / 1024).toFixed(0)}KB)</span>
                        <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="ml-1 text-red-400 hover:text-red-300 font-bold" data-testid={`remove-file-${i}`}>x</button>
                      </div>
                    ))}
                  </div>
                )}

                {uploading && (
                  <div className="mb-3 flex items-center gap-3">
                    <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: 'transparent' }} />
                    <span className="font-mono text-xs" style={{ color: accent }}>{uploadProgress}</span>
                  </div>
                )}

                <button onClick={createEntry} data-testid="btn-submit-entry" disabled={uploading}
                  className="font-mono text-xs font-bold px-6 py-2 rounded-lg transition-opacity"
                  style={{ background: accent, color: isDark ? '#000' : '#fff', opacity: uploading ? 0.5 : 1 }}>
                  {uploading ? 'Caricamento...' : uploadFiles.length > 0 ? `Invia Contributo + ${uploadFiles.length} file` : 'Invia Contributo'}
                </button>
              </div>
            )}
            {entries.length === 0 ? (
              <p className={`${textSub} text-sm text-center py-12`}>Nessun contributo ancora. Sii il primo a raccogliere sapienza!</p>
            ) : (
              <div className="grid gap-3">
                {entries.map(e => {
                  const files = getEntryFiles(e.id, e.team_id);
                  return (
                    <div key={e.id} className={`${cardBg} border rounded-xl p-4`} data-testid="archive-entry-card">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono font-bold text-sm">{e.title}</p>
                          <p className={`${textSub} text-xs mt-1 line-clamp-2`}>{e.description}</p>
                          <div className="flex gap-2 mt-2">
                            <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: accent + '44', color: accent }}>{e.category}</span>
                            {e.mentor_name && <span className={`text-[10px] ${textSub}`}>Mentore: {e.mentor_name}</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-mono font-black text-lg" style={{ color: accent }}>+{e.points}</p>
                          <p className={`${textSub} text-[10px]`}>{e.team_name}</p>
                        </div>
                      </div>
                      {/* Attached files gallery */}
                      {files.length > 0 && (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: isDark ? '#1a1a1a' : '#E2DED6' }}>
                          <p className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: accent + '88' }}>
                            {files.length} file allegat{files.length === 1 ? 'o' : 'i'}
                          </p>
                          <div className="flex flex-wrap gap-2" data-testid="entry-files-gallery">
                            {files.map(f => (
                              <FileThumb key={f.id} file={f} fileUrl={fileUrl} isDark={isDark} accent={accent} textSub={textSub} onLightbox={setLightbox} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB: Squadre */}
        {tab === 'squadre' && (
          <div>
            <p className="font-mono font-bold mb-4" style={{ color: accent }}>5 Team in competizione</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.filter(t => t.role === 'competitor').map(t => {
                const tFiles = getTeamFiles(t.id);
                const isExpanded = galleryTeam === t.id;
                return (
                  <div key={t.id} className={`${cardBg} border rounded-xl p-5 relative overflow-hidden`} data-testid="team-card">
                    <div className="absolute top-0 left-0 w-1.5 h-full" style={{ background: t.color }} />
                    <div className="flex justify-between items-start pl-3">
                      <div>
                        <p className="font-mono font-black text-lg" style={{ color: t.color }}>{t.name}</p>
                        <p className={`${textSub} text-xs mt-1`}>{t.members?.length || 0} membri</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-black text-2xl" style={{ color: t.color }}>{t.points}</p>
                        <p className={`${textSub} text-[10px] uppercase`}>punti</p>
                        <p className={`${textSub} text-[10px]`}>{t.entries_count} contributi</p>
                      </div>
                    </div>
                    {/* File gallery for team */}
                    {tFiles.length > 0 && (
                      <div className="mt-4 pl-3">
                        <button
                          onClick={() => setGalleryTeam(isExpanded ? null : t.id)}
                          className="font-mono text-[10px] flex items-center gap-1.5 mb-2 transition-colors"
                          style={{ color: t.color }}
                          data-testid={`team-gallery-toggle-${t.id}`}
                        >
                          <svg className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path d="M6 6l8 4-8 4z"/></svg>
                          {tFiles.length} file caricati
                        </button>
                        {isExpanded && (
                          <div className="grid grid-cols-3 gap-2 mt-2" data-testid={`team-gallery-${t.id}`}>
                            {tFiles.map(f => (
                              <FileThumb key={f.id} file={f} fileUrl={fileUrl} isDark={isDark} accent={t.color} textSub={textSub} compact onLightbox={setLightbox} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TAB: Classifica */}
        {tab === 'classifica' && (
          <div>
            <p className="font-mono font-bold mb-4" style={{ color: accent }}>&lt;/classifica_annuale&gt;</p>
            {leaderboard.length === 0 ? (
              <p className={`${textSub} text-sm text-center py-12`}>Classifica vuota. Inizia a raccogliere sapienza!</p>
            ) : (
              <div className="space-y-3">
                {leaderboard.filter(t => t.role === 'competitor').map((t, i) => {
                  const fCount = (teamFiles[t.id] || []).length;
                  return (
                  <div key={t.id} className={`${cardBg} border rounded-xl p-4 flex items-center gap-4`} data-testid="leaderboard-row">
                    <span className="font-mono font-black text-3xl w-12 text-center" style={{ color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : accent + '66' }}>
                      {i + 1}
                    </span>
                    <div className="w-3 h-12 rounded-full" style={{ background: t.color }} />
                    <div className="flex-1">
                      <p className="font-mono font-bold">{t.name}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className={`${textSub} text-xs`}>{t.entries_count} contributi</span>
                        {fCount > 0 && (
                          <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: t.color + '18', color: t.color }} data-testid="file-count-badge">
                            {fCount} file
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="font-mono font-black text-xl" style={{ color: t.color }}>{t.points} pts</p>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB: Premi */}
        {tab === 'premi' && (
          <div>
            <p className="font-mono font-bold mb-2" style={{ color: accent }}>&lt;/10_premi_annuali&gt;</p>
            <p className={`${textSub} text-sm mb-6`}>I 10 migliori team ricevono un premio reale a fine anno. La sapienza vince sempre.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {prizes.map((p, i) => (
                <div key={i} className={`${cardBg} border rounded-xl p-4 flex items-center gap-4`} data-testid="prize-card">
                  <span className="font-mono font-black text-2xl w-10 text-center" style={{ color: i < 3 ? '#FFD700' : accent }}>
                    {p.rank}
                  </span>
                  <p className="font-mono font-bold text-sm">{p.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ═══ LIGHTBOX ═══ */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.92)' }} onClick={() => setLightbox(null)} data-testid="lightbox-overlay">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white/70 hover:text-white font-mono text-2xl z-10" data-testid="lightbox-close">x</button>
          <div className="max-w-4xl max-h-[85vh] w-full" onClick={e => e.stopPropagation()}>
            {lightbox.type === 'image' && (
              <img src={lightbox.url} alt={lightbox.name} className="w-full h-auto max-h-[80vh] object-contain rounded-xl" />
            )}
            {lightbox.type === 'video' && (
              <video src={lightbox.url} controls autoPlay playsInline className="w-full max-h-[80vh] rounded-xl" />
            )}
            {lightbox.type === 'audio' && (
              <div className="bg-[#111] rounded-xl p-8 text-center">
                <p className="font-mono text-lg text-white mb-4">{lightbox.name}</p>
                <audio src={lightbox.url} controls autoPlay className="w-full" />
              </div>
            )}
            <p className="font-mono text-[10px] text-zinc-500 text-center mt-3">{lightbox.name}</p>
          </div>
        </div>
      )}
    </main>
  );
}
